import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getInviteByToken } from "@/lib/api/invites";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCorrectUser, setIsCorrectUser] = useState(false); // User email matches invite email
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    async function loadInvite() {
      if (!token) {
        setError("Ungültiger Einladungslink.");
        setLoading(false);
        return;
      }

      try {
        // Check if user is logged in
        const { data: { user } } = await supabase.auth.getUser();
        setIsLoggedIn(!!user);
        setCurrentUserEmail(user?.email || null);

        const inviteData = await getInviteByToken(token);
        
        // Check if invite is expired
        if (new Date(inviteData.expires_at) < new Date()) {
          setError("Diese Einladung ist abgelaufen.");
          setLoading(false);
          return;
        }

        // Check if already accepted
        if (inviteData.accepted_at) {
          setError("Diese Einladung wurde bereits angenommen.");
          setLoading(false);
          return;
        }

        setInvite(inviteData);
        
        // Check if logged in user email matches invite email
        if (user && user.email?.toLowerCase() === inviteData.email.toLowerCase()) {
          setIsCorrectUser(true);
        }
      } catch (err: any) {
        setError("Einladung nicht gefunden.");
      } finally {
        setLoading(false);
      }
    }

    loadInvite();
  }, [token]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (signupData.password !== signupData.confirmPassword) {
      toast({
        title: "Passwörter stimmen nicht überein",
        description: "Bitte überprüfen Sie Ihre Passworteingabe.",
        variant: "destructive",
      });
      return;
    }

    if (signupData.password.length < 8) {
      toast({
        title: "Passwort zu kurz",
        description: "Das Passwort muss mindestens 8 Zeichen lang sein.",
        variant: "destructive",
      });
      return;
    }

    setAccepting(true);
    try {
      // Use server-side Edge Function for secure invite acceptance
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: {
          token,
          password: signupData.password,
          firstName: signupData.firstName,
          lastName: signupData.lastName,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Willkommen!",
        description: `Ihr Account wurde erstellt und Sie sind jetzt Mitglied von ${data.workspaceName || invite?.workspaces?.name}.`,
      });

      // Sign in the new user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password: signupData.password,
      });

      if (signInError) {
        console.error('Auto sign-in failed:', signInError);
        navigate('/auth');
        return;
      }

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten';
      toast({
        title: "Fehler bei der Registrierung",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleLogoutAndCreateAccount = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setIsCorrectUser(false);
    setCurrentUserEmail(null);
    setShowSignup(true);
  };

  const handleAccept = async () => {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setShowSignup(true);
      return;
    }

    // CRITICAL: Only allow accepting if email matches
    if (user.email?.toLowerCase() !== invite?.email?.toLowerCase()) {
      toast({
        title: "Falsche E-Mail-Adresse",
        description: "Sie sind mit einer anderen E-Mail-Adresse angemeldet als die Einladung.",
        variant: "destructive",
      });
      return;
    }

    setAccepting(true);
    try {
      // Use server-side Edge Function for secure invite acceptance
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: {
          token,
          existingUserId: user.id,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Einladung angenommen!",
        description: `Sie sind jetzt Mitglied von ${data.workspaceName || invite?.workspaces?.name}.`,
      });

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten';
      toast({
        title: "Fehler beim Annehmen der Einladung",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Fehler</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">Zur Startseite</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show signup form if user is not logged in
  if (showSignup && !isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Account erstellen</CardTitle>
            <CardDescription>
              Erstellen Sie einen Account, um der Einladung von <span className="font-semibold">{invite?.workspaces?.name}</span> beizutreten.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={invite?.email || ""}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Vorname</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={signupData.firstName}
                    onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })}
                    required
                    disabled={accepting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nachname</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={signupData.lastName}
                    onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })}
                    required
                    disabled={accepting}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  required
                  disabled={accepting}
                  minLength={8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  required
                  disabled={accepting}
                  minLength={8}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Button type="submit" className="w-full" disabled={accepting}>
                  {accepting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : (
                    "Account erstellen und Einladung annehmen"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/auth')}
                  disabled={accepting}
                  className="w-full"
                >
                  Bereits ein Account? Anmelden
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Einladung zum Workspace</CardTitle>
          <CardDescription>
            Sie wurden zu <span className="font-semibold">{invite?.workspaces?.name}</span> eingeladen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">E-Mail:</span>
              <span className="font-medium">{invite?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rolle:</span>
              <span className="font-medium capitalize">
                {invite?.role === 'admin' ? 'Admin' : 'Mitglied'}
              </span>
            </div>
          </div>

          {isLoggedIn && isCorrectUser ? (
            // User is logged in with the correct email
            <Button 
              className="w-full" 
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird angenommen...
                </>
              ) : (
                "Einladung annehmen"
              )}
            </Button>
          ) : isLoggedIn && !isCorrectUser ? (
            // User is logged in but with a different email
            <div className="flex flex-col gap-3">
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm">
                <p className="font-medium text-destructive">Falsche E-Mail-Adresse</p>
                <p className="text-muted-foreground mt-1">
                  Sie sind als <span className="font-medium">{currentUserEmail}</span> angemeldet, 
                  aber diese Einladung ist für <span className="font-medium">{invite?.email}</span>.
                </p>
              </div>
              <Button 
                className="w-full" 
                onClick={handleLogoutAndCreateAccount}
              >
                Abmelden und neuen Account erstellen
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/auth');
                }}
                className="w-full"
              >
                Mit {invite?.email} anmelden
              </Button>
            </div>
          ) : (
            // User is not logged in
            <div className="flex flex-col gap-2">
              <Button 
                className="w-full" 
                onClick={handleAccept}
                disabled={accepting}
              >
                Account erstellen und beitreten
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/auth')}
                className="w-full"
              >
                Bereits ein Account? Anmelden
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
